import { useMemo, useState, type CSSProperties } from 'react';
import { Chessboard, defaultPieces } from 'react-chessboard';
import { Chess, type Square } from 'chess.js';
import type { Color, PromotionPiece } from '../../shared/protocol.ts';
import { BOARD_THEMES, usePreferences } from '../app/preferences.ts';
import { checkedKingSquare, isPromotionMove, legalTargets, type MoveLike } from '../lib/chess.ts';
import { sound } from '../lib/sound.ts';

interface BoardProps {
  fen: string;
  orientation: Color;
  /** Colour the user may move, or null for a read-only board. */
  movableColor: Color | null;
  lastMove?: { from: string; to: string } | null;
  hint?: { from: string; to: string } | null;
  /** Visual cue that an earlier position is being shown. */
  historical?: boolean;
  onMove?: (move: MoveLike) => void;
}

const PROMOTION_PIECES: PromotionPiece[] = ['q', 'r', 'b', 'n'];

export function Board({ fen, orientation, movableColor, lastMove, hint, historical, onMove }: BoardProps) {
  const { prefs, t } = usePreferences();
  const theme = BOARD_THEMES[prefs.boardTheme];
  const chess = useMemo(() => new Chess(fen), [fen]);

  // Selection and pending promotion belong to one position; they vanish when it changes.
  const positionKey = `${fen}|${movableColor}`;
  const [selection, setSelection] = useState<{ key: string; square: string } | null>(null);
  const [pendingPromotion, setPendingPromotion] = useState<{ key: string; from: string; to: string } | null>(null);
  const selected = selection?.key === positionKey ? selection.square : null;
  const promotion = pendingPromotion?.key === positionKey ? pendingPromotion : null;
  const setSelected = (square: string | null) => setSelection(square ? { key: positionKey, square } : null);
  const setPromotion = (move: { from: string; to: string } | null) =>
    setPendingPromotion(move ? { key: positionKey, ...move } : null);

  const canMove = movableColor !== null && chess.turn() === movableColor && !chess.isGameOver();
  const targets = useMemo(() => (selected ? legalTargets(chess, selected) : []), [chess, selected]);

  const tryMove = (from: string, to: string): boolean => {
    if (!canMove || !legalTargets(chess, from).includes(to)) return false;
    if (isPromotionMove(chess, from, to)) {
      if (prefs.autoQueen) {
        onMove?.({ from, to, promotion: 'q' });
      } else {
        setPromotion({ from, to });
        return false; // piece snaps back until a piece is chosen
      }
    } else {
      onMove?.({ from, to });
    }
    setSelected(null);
    return true;
  };

  const ownPiece = (square: string) => {
    const piece = chess.get(square as Square);
    return Boolean(piece && piece.color === movableColor);
  };

  const handleSquareClick = (square: string) => {
    if (!canMove || promotion) return;
    if (selected && selected !== square && targets.includes(square)) {
      tryMove(selected, square);
      return;
    }
    if (ownPiece(square) && selected !== square) {
      setSelected(square);
      return;
    }
    if (selected && selected !== square && !ownPiece(square)) sound.play('illegal');
    setSelected(null);
  };

  const squareStyles = useMemo(() => {
    const styles: Record<string, CSSProperties> = {};
    if (lastMove) {
      styles[lastMove.from] = { backgroundColor: theme.lastMove };
      styles[lastMove.to] = { backgroundColor: theme.lastMove };
    }
    const king = checkedKingSquare(chess);
    if (king) {
      styles[king] = {
        ...styles[king],
        backgroundImage: 'radial-gradient(circle, rgba(230, 60, 50, 0.95) 0%, rgba(230, 60, 50, 0.55) 45%, transparent 72%)',
      };
    }
    if (selected) styles[selected] = { ...styles[selected], backgroundColor: 'rgba(255, 200, 40, 0.75)' };
    if (prefs.legalMoves) {
      // Big green dots for "you can go here", a red ring for "you can capture this".
      for (const sq of targets) {
        const occupied = chess.get(sq as Square);
        styles[sq] = {
          ...styles[sq],
          backgroundImage: occupied
            ? 'radial-gradient(circle, transparent 54%, rgba(234, 75, 75, 0.85) 56%, rgba(234, 75, 75, 0.85) 66%, transparent 68%)'
            : 'radial-gradient(circle, rgba(34, 179, 94, 0.85) 24%, rgba(255, 255, 255, 0.9) 25%, rgba(255, 255, 255, 0.9) 28%, transparent 29%)',
        };
      }
    }
    return styles;
  }, [chess, lastMove, selected, targets, prefs.legalMoves, theme.lastMove]);

  const arrows = useMemo(
    () => (hint ? [{ startSquare: hint.from, endSquare: hint.to, color: 'rgba(255, 170, 0, 0.95)' }] : []),
    [hint],
  );

  const notation: CSSProperties = { fontSize: 'clamp(11px, 1.8vw, 16px)', fontWeight: 800, fontFamily: 'var(--font-sans)' };

  return (
    <div className={`board-frame${historical ? ' is-history' : ''}`}>
      <Chessboard
        options={{
          id: 'outchess-board',
          position: fen,
          boardOrientation: orientation === 'w' ? 'white' : 'black',
          lightSquareStyle: { backgroundColor: theme.light },
          darkSquareStyle: { backgroundColor: theme.dark },
          lightSquareNotationStyle: { ...notation, color: theme.dark },
          darkSquareNotationStyle: { ...notation, color: theme.light },
          showNotation: prefs.coordinates,
          squareStyles,
          arrows,
          allowDrawingArrows: true,
          clearArrowsOnPositionChange: true,
          animationDurationInMs: 160,
          dragActivationDistance: 4,
          allowDragOffBoard: false,
          canDragPiece: ({ piece }) => canMove && piece.pieceType[0] === movableColor,
          onPieceDrag: ({ square }) => {
            if (square && canMove) setSelected(square);
          },
          onPieceDrop: ({ sourceSquare, targetSquare }) => {
            if (!targetSquare || sourceSquare === targetSquare) return false;
            const ok = tryMove(sourceSquare, targetSquare);
            if (!ok && !promotion && !isPromotionMove(chess, sourceSquare, targetSquare)) sound.play('illegal');
            return ok;
          },
          onSquareClick: ({ square }) => handleSquareClick(square),
        }}
      />

      {promotion && movableColor && (
        <div className="promotion-overlay" onClick={() => setPromotion(null)}>
          <div
            className="promotion-picker"
            role="dialog"
            aria-label={t('promotion.title')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="promotion-title">{t('promotion.title')}</div>
            <div className="promotion-options">
              {PROMOTION_PIECES.map((p, i) => {
                const render = defaultPieces[`${movableColor}${p.toUpperCase()}`];
                return (
                  <button
                    key={p}
                    type="button"
                    className="promotion-option"
                    autoFocus={i === 0}
                    onClick={() => {
                      onMove?.({ from: promotion.from, to: promotion.to, promotion: p });
                      setPromotion(null);
                      setSelected(null);
                    }}
                  >
                    {render?.()}
                    <span>{t(`piece.${p}`)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
